import axios from 'axios';

// API基础路径
const API_BASE_URL = '/api/area';

// 地区信息接口定义
export interface CosArea {
  id: number;
  topId?: number;
  pid?: number;
  name?: string;
  shortname?: string;
  pinyin?: string;
  level?: number;
  sort?: number;
  isHot?: number;
  status?: number;
  addTime?: number;
  cosArea?: any;
}

export interface CosAreaListResponse {
  code: number;
  msg?: string;
  message?: string;
  data?: {
    total: number;
    list: CosArea[];
  };
}

export interface CosAreaQueryParams {
  level?: number; // 层级：1省份 2城市 3区域
  topId?: number; // 大区ID：1华北 2华东 3华南 4中南 5东北 6西南 7西北 8其他
  pid?: number; // 上级ID（用于查询下级地区）
  name?: string; // 名称（搜索）
  isHot?: number; // 是否热门
  status?: number; // 状态
  pageNum?: number; // 页码（可选，仅在需要分页时传入）
  pageSize?: number; // 每页条数（可选，仅在需要分页时传入）
}

class AreaApi {
  /**
   * 获取地区列表（从缓存）
   */
  async getAreaList(params?: CosAreaQueryParams): Promise<CosAreaListResponse> {
    try {
      // 构建查询参数
      const queryParams = new URLSearchParams();

      if (params?.level !== undefined) queryParams.append('level', String(params.level));
      if (params?.topId !== undefined) queryParams.append('topId', String(params.topId));
      if (params?.pid !== undefined) queryParams.append('pid', String(params.pid));
      if (params?.name) queryParams.append('name', params.name);
      if (params?.isHot !== undefined) queryParams.append('isHot', String(params.isHot));
      if (params?.status !== undefined) queryParams.append('status', String(params.status));
      if (params?.pageNum !== undefined) queryParams.append('pageNum', String(params.pageNum));
      if (params?.pageSize !== undefined) queryParams.append('pageSize', String(params.pageSize));

      const url = `${API_BASE_URL}/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [areaApi] 准备请求地区列表:', url);
      }

      // 使用 axios 发送 GET 请求
      const axiosInstance = axios.create({
        baseURL: '', // 使用相对路径，让代理生效
        timeout: 30000,
      });

      const response = await axiosInstance.get<CosAreaListResponse>(url, {
        headers: {
          'Accept': 'application/json, text/plain, */*',
        },
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ [areaApi] 地区列表请求成功, total:', response.data.data?.total);
      }

      // 检查响应 code（code === 1 表示成功）
      if (response.data.code === 1) {
        // 后端返回格式：{ code: 1, message: '查询成功', data: { total: number, list: CosArea[] }, meta: {...} }
        return {
          code: response.data.code,
          message: response.data.message,
          msg: response.data.msg,
          data: response.data.data,
        };
      } else {
        throw new Error(response.data.msg || response.data.message || '获取地区列表失败');
      }
    } catch (error: any) {
      console.error('❌ 获取地区列表失败:', error);
      console.error('❌ 错误详情:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
      });
      return {
        code: 0,
        msg: error.response?.data?.msg || error.response?.data?.message || error.message || '获取地区列表失败',
        data: { total: 0, list: [] },
      };
    }
  }

  /**
   * 获取所有省份（level=1）
   */
  async getProvinces(): Promise<CosArea[]> {
    const response = await this.getAreaList({ level: 1, status: 1 });
    return response.data?.list || [];
  }

  /**
   * 根据省份ID获取城市列表（level=2, pid=省份ID）
   */
  async getCitiesByProvince(provinceId: number): Promise<CosArea[]> {
    const response = await this.getAreaList({ level: 2, pid: provinceId, status: 1 });
    return response.data?.list || [];
  }

  /**
   * 获取全部城市，用于命令解析等需按城市名匹配的场景（如「沈阳」→ 沈阳市）
   * 先尝试 level=2 不传 pid；若为空则按各省份拉取城市后合并
   */
  async getAllCities(): Promise<CosArea[]> {
    let list = (await this.getAreaList({ level: 2, status: 1 })).data?.list || [];
    if (list.length === 0) {
      const provinces = await this.getProvinces();
      const allCities: CosArea[] = [];
      await Promise.all(
        provinces.map(async (p) => {
          const cities = await this.getCitiesByProvince(p.id);
          allCities.push(...cities);
        })
      );
      list = allCities;
    }
    return list;
  }

  /**
   * 获取所有大区（topId）
   */
  async getRegions(): Promise<CosArea[]> {
    // 大区是 topId，需要通过查询省份来获取大区列表
    const provinces = await this.getProvinces();
    // 按 topId 分组，获取所有大区
    const topIdMap = new Map<number, CosArea>();
    provinces.forEach(province => {
      if (province.topId && !topIdMap.has(province.topId)) {
        topIdMap.set(province.topId, {
          id: province.topId,
          topId: province.topId,
          name: this.getRegionName(province.topId),
          level: 0, // 大区层级为0
        });
      }
    });
    return Array.from(topIdMap.values()).sort((a, b) => (a.topId || 0) - (b.topId || 0));
  }

  /**
   * 根据大区ID获取大区名称
   */
  private getRegionName(topId: number): string {
    const regionMap: Record<number, string> = {
      1: '华北',
      2: '华东',
      3: '华南',
      4: '中南',
      5: '东北',
      6: '西南',
      7: '西北',
      8: '其他',
    };
    return regionMap[topId] || `大区${topId}`;
  }
}

const areaApi = new AreaApi();
export default areaApi;
